import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/client';
import type { Company, Member } from '../lib/models';
import { isPersonalWorkspaceId } from '../lib/models';
import {
  companyFromSnapshot,
  memberFromSnapshot,
} from '../lib/firestoreCodec';
import {
  companyWorkspaceIsOnHold,
  premiumPaidThroughOneMonthFromNow,
} from '../lib/billing';

const D = () => db();

export async function getCompany(companyId: string): Promise<Company | null> {
  const ref = doc(D(), 'companies', companyId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return companyFromSnapshot(snap);
}

export async function getCompaniesForUser(uid: string): Promise<Company[]> {
  const q = query(
    collection(D(), 'companies'),
    where('memberIds', 'array-contains', uid),
  );
  const snap = await getDocs(q);
  const out: Company[] = [];
  for (const d of snap.docs) {
    if (isPersonalWorkspaceId(d.id)) continue;
    const memberRef = doc(D(), 'companies', d.id, 'members', uid);
    const mSnap = await getDoc(memberRef);
    const bg = mSnap.exists()
      ? (mSnap.data()?.backgroundAsset as string | undefined)
      : undefined;
    out.push(
      companyFromSnapshot(d, bg ?? 'assets/images/background.png'),
    );
  }
  out.sort((a, b) =>
    a.name.trim().localeCompare(b.name.trim(), undefined, { sensitivity: 'base' }),
  );
  return out;
}

export async function getCompanyMembers(
  companyId: string,
): Promise<Member[]> {
  const col = collection(D(), 'companies', companyId, 'members');
  const snap = await getDocs(col);
  return snap.docs.map((d) => memberFromSnapshot(d.id, d.data()));
}

/** Live member list — updates when display names or roles change. */
export function watchCompanyMembers(
  companyId: string,
  onNext: (members: Member[]) => void,
): Unsubscribe {
  const col = collection(D(), 'companies', companyId, 'members');
  return onSnapshot(col, (snap) => {
    onNext(snap.docs.map((d) => memberFromSnapshot(d.id, d.data())));
  });
}

export function watchCompanyMember(
  companyId: string,
  uid: string,
  onNext: (member: Member | null) => void,
): Unsubscribe {
  const ref = doc(D(), 'companies', companyId, 'members', uid);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      onNext(null);
      return;
    }
    onNext(memberFromSnapshot(uid, snap.data()!));
  });
}

export async function getCompanyMember(
  companyId: string,
  uid: string,
): Promise<Member | null> {
  const ref = doc(D(), 'companies', companyId, 'members', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return memberFromSnapshot(uid, snap.data()!);
}

export async function getMemberRole(
  companyId: string,
  uid: string,
): Promise<Member['role'] | null> {
  const member = await getCompanyMember(companyId, uid);
  return member?.role ?? null;
}

/** Stamps a one-month paid-through date on owned Premium workspaces that have none. */
export async function ensureOwnedPremiumEndDates(uid: string): Promise<void> {
  if (!uid.trim()) return;
  const companies = await getCompaniesForUser(uid);
  const paidThrough = premiumPaidThroughOneMonthFromNow();
  for (const company of companies) {
    if ((company.billingPlan ?? 'premium').toLowerCase() !== 'premium') continue;
    if (
      company.premiumSubscriptionPaidThrough != null ||
      company.premiumComplimentaryEndsAt != null
    ) {
      continue;
    }
    const role = await getMemberRole(company.id, uid);
    if (role !== 'owner') continue;
    const ref = doc(D(), 'companies', company.id);
    const snap = await getDoc(ref);
    const data = snap.data();
    if (!data) continue;
    if (
      data.premiumSubscriptionPaidThrough != null ||
      data.premiumComplimentaryEndsAt != null ||
      data.premiumIntroductoryOfferEndAt != null
    ) {
      continue;
    }
    await updateDoc(ref, { premiumSubscriptionPaidThrough: paidThrough });
  }
}

export async function findFirstPremiumLapsedOwnedCompanyId(
  uid: string,
): Promise<string | null> {
  if (!uid.trim()) return null;
  await ensureOwnedPremiumEndDates(uid);
  const companies = await getCompaniesForUser(uid);
  const candidates = companies
    .filter(
      (c) =>
        (c.billingPlan ?? 'premium').toLowerCase() === 'premium' &&
        companyWorkspaceIsOnHold(c),
    )
    .sort((a, b) => a.id.localeCompare(b.id));
  for (const c of candidates) {
    const role = await getMemberRole(c.id, uid);
    if (role === 'owner') return c.id;
  }
  return null;
}

export function userHasAccessibleWorkspace(companies: Company[]): boolean {
  return companies.some((c) => !companyWorkspaceIsOnHold(c));
}

export interface CreateCompanyInput {
  name: string;
  businessName?: string;
  colorHex: number;
  createdBy: string;
  creatorPhoneNumber?: string;
  creatorDisplayName?: string;
  billingPlan?: string;
}

/**
 * Matches `FirestoreService.createCompany` core fields (client-side create).
 * Billing / premium edge cases are enforced by Firestore rules and Cloud Functions.
 */
export async function createCompany(input: CreateCompanyInput): Promise<string> {
  const ref = doc(collection(D(), 'companies'));
  const now = new Date();
  const companyMap: Record<string, unknown> = {
    name: input.name,
    colorHex: input.colorHex,
    createdAt: now,
    createdBy: input.createdBy,
    memberIds: [input.createdBy],
    billingPlan: input.billingPlan ?? 'premium',
    ...((input.billingPlan ?? 'premium').toLowerCase() === 'premium'
      ? { premiumSubscriptionPaidThrough: premiumPaidThroughOneMonthFromNow() }
      : {}),
    // Workspace settings defaults for new companies (parity with mobile createCompany).
    autoSelectSupervisorsOnNewTasks: true,
    allowChatMessageEdit: true,
    chatMessageEditWindowMinutes: 30,
    allowChatMessageDelete: false,
    chatMessageDeleteWindowMinutes: 30,
  };
  if (input.businessName?.trim()) {
    companyMap.businessName = input.businessName.trim();
  }
  await setDoc(ref, companyMap);
  const memberPayload: Record<string, unknown> = {
    role: 'owner',
    joinedAt: now,
  };
  if (input.creatorPhoneNumber) {
    memberPayload.phoneNumber = input.creatorPhoneNumber;
  }
  if (input.creatorDisplayName) {
    memberPayload.displayName = input.creatorDisplayName;
  }
  await setDoc(doc(D(), 'companies', ref.id, 'members', input.createdBy), memberPayload);
  return ref.id;
}

export function watchCompany(
  companyId: string,
  onNext: (c: Company | null) => void,
): Unsubscribe {
  const ref = doc(D(), 'companies', companyId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      onNext(null);
      return;
    }
    onNext(companyFromSnapshot(snap));
  });
}

export async function getCompanyTagNames(companyId: string): Promise<string[]> {
  const ref = doc(D(), 'companies', companyId);
  const snap = await getDoc(ref);
  const tagNames = snap.data()?.tagNames;
  if (!Array.isArray(tagNames)) return [];
  return tagNames
    .map((e) => String(e).trim())
    .filter(Boolean)
    .sort();
}

export async function updateCompanyFields(
  companyId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await updateDoc(
    doc(D(), 'companies', companyId),
    patch as DocumentData,
  );
}

/** Owner-only destructive action; matches mobile `FirestoreService.deleteCompany` (batched). */
export async function deleteCompanyAndMembers(companyId: string): Promise<void> {
  const cref = doc(D(), 'companies', companyId);
  const snap = await getDocs(collection(D(), 'companies', companyId, 'members'));
  const memberRefs = snap.docs.map((d) => d.ref);
  const chunk = 400;
  let idx = 0;
  while (idx < memberRefs.length) {
    const batch = writeBatch(D());
    const end = Math.min(idx + chunk, memberRefs.length);
    for (let i = idx; i < end; i++) {
      batch.delete(memberRefs[i]);
    }
    idx = end;
    if (idx >= memberRefs.length) {
      batch.delete(cref);
    }
    await batch.commit();
  }
  if (memberRefs.length === 0) {
    await deleteDoc(cref);
  }
}
